import React, { useEffect, useRef, useState } from 'react';
import {
  FaPlus as Plus,
  FaTrash as Trash2,
  FaChevronRight as ChevronRight,
  FaCalendarAlt as Calendar,
  FaMapMarkerAlt as MapPin,
  FaUsers as Users,
  FaUserPlus as UserPlus,
  FaChevronDown as ChevronDown,
  FaChevronUp as ChevronUp,
  FaMinus as Minus,
  FaTimes as X,
  FaInfoCircle as InfoCircle,
} from "react-icons/fa";
import * as bootstrap from 'bootstrap'; 

// Utility function (keep here or move to a separate utils file)
const getCategoryClass = (category) => {
  const classes = {
    Student: 'badge badge-student',
    Workshop: 'badge badge-workshop',
    Professional: 'badge badge-professional',
    General: 'badge badge-general',
  };
  return classes[category] || 'badge badge-general';
};

const TicketCard = React.memo(({ 
  ticket, 
  updateTicketQuantity, 
  updateGuestCount, 
  toggleGuestExpansion, 
  toggleAddon, 
  updateAddonQuantity, 
  selectedCurrency, 
  allowBuyers, 
  taxName, 
  expandedGuestCard, 
  tooltip, 
  setTooltip,
  stateIsSame,
  hasOtherTicketsSelected,
  allowMultipleTickets
}) => {
  const isMainTicketCategory = ticket.category !== 'Workshop';
  const isGuestExpanded = expandedGuestCard === ticket.id;
  const isCurrencyMismatch = selectedCurrency && ticket.currency !== selectedCurrency;
 const maxTickets =
  ticket.balance === 100000 || ticket.balance === Infinity
    ? "Infinity"
    : ticket.balance;

  const [showFullDescription, setShowFullDescription] = useState(false); // State to toggle description

  // Refs for Bootstrap tooltips
  const ticketTooltipRef = useRef(null);
  const guestTooltipRef = useRef(null);

  // Initialize Bootstrap tooltips
  useEffect(() => {
    const ticketTooltipElement = ticketTooltipRef.current;
    const guestTooltipElement = guestTooltipRef.current;

    let ticketTooltipInstance = null;
    let guestTooltipInstance = null;

    if (ticketTooltipElement) {
      ticketTooltipInstance = new bootstrap.Tooltip(ticketTooltipElement, {
        title: stateIsSame
          ? 'SGST + CGST is applied because the organizer and event location are in the same state.'
          : 'IGST is applied because the organizer and event location are in different states.',
        placement: 'top',
        trigger: 'hover',
        container: 'body',
      });
    }

    if (guestTooltipElement) {
      guestTooltipInstance = new bootstrap.Tooltip(guestTooltipElement, {
        title: stateIsSame
          ? 'SGST + CGST is applied because the organizer and event location are in the same state.'
          : 'IGST is applied because the organizer and event location are in different states.',
        placement: 'top',
        trigger: 'hover',
        container: 'body',
      });
    }

    // Cleanup tooltips on component unmount
    return () => {
      if (ticketTooltipInstance) ticketTooltipInstance.dispose();
      if (guestTooltipInstance) guestTooltipInstance.dispose();
    };
  }, [stateIsSame]);

  // Filter tax_options based on stateIsSame
  const tax_options = typeof ticket.tax_options === 'string' ? JSON.parse(ticket.tax_options) : ticket.tax_options || [];
  const relevantTaxes = stateIsSame
    ? tax_options.filter((t) => ['CGST', 'SGST'].includes(t.tax_name))
    : tax_options.filter((t) => t.tax_name === 'IGST');
  const rate = relevantTaxes.reduce(
    (sum, t) => sum + (Number.parseFloat(t.tax_percentage) || 0),
    0
  ) / 100;

  let showPrice = ticket.price;
  let priceDetails = '';
  if (ticket.category === 'Free') {
    showPrice = 0;
    priceDetails = 'Free';
  } else if (ticket.tax_type === 'No Tax') {
    priceDetails = 'No Tax';
  } else if (ticket.tax_type === 'Including Tax') {
    priceDetails = `Incl. ${taxName}`;
  } else if (ticket.tax_type === 'Excluding Tax') {
    showPrice = ticket.price + ticket.price * rate;
    priceDetails = `Excl. ${taxName}`;
  }
  showPrice = Math.round(showPrice * 100) / 100;

  let guestShowPrice = ticket.guestPrice;
  let guestPriceDetails = '';
  if (ticket.guest_details) {
    const g_tax_options = typeof ticket.guest_details.tax_options === 'string' ? JSON.parse(ticket.guest_details.tax_options) : ticket.guest_details.tax_options || [];
    const g_relevantTaxes = stateIsSame
      ? g_tax_options.filter((t) => ['CGST', 'SGST'].includes(t.tax_name))
      : g_tax_options.filter((t) => t.tax_name === 'IGST');
    const g_rate = g_relevantTaxes.reduce(
      (sum, t) => sum + (Number.parseFloat(t.tax_percentage) || 0),
      0
    ) / 100;
    const g_tax_type = ticket.guest_details.tax_type || 'No Tax';
    if (g_tax_type === 'No Tax') {
      guestPriceDetails = 'No Tax';
    } else if (g_tax_type === 'Including Tax') {
      guestPriceDetails = `Incl. ${taxName}`;
    } else if (g_tax_type === 'Excluding Tax') {
      guestShowPrice = ticket.guestPrice + ticket.guestPrice * g_rate;
      guestPriceDetails = `Excl. ${taxName}`;
    }
    guestShowPrice = Math.round(guestShowPrice * 100) / 100;
  }

  // Determine if description is long enough to show "More" button
  const isTicketDescriptionLong = ticket.description && ticket.description.length > 100; // Adjust threshold as needed
  const isGuestDescriptionLong = ticket.guest_details?.description && ticket.guest_details.description.length > 100;

  return (
    <div className="ticket-card">
      <div className="ticket-content">
        <div className="ticket-info">
          <div className="ticket-header">
            <h4 className="ticket-title">{ticket.title}</h4>
            <span className={getCategoryClass(ticket.category)}>
              {ticket.category}
            </span>
          </div>
          <div className="ticket-description-container">
            <p 
              className="ticket-subtitle" 
              style={{
                display: '-webkit-box',
                WebkitLineClamp: showFullDescription ? 'unset' : 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: showFullDescription ? 'normal' : 'normal',
                lineHeight: '1.5em',
                maxHeight: showFullDescription ? 'none' : '1.5em',
              }}
            >
              {ticket.description || ''}
            </p>
            {isTicketDescriptionLong && (
              <button
                className="btn btn-link p-0 text-primary"
                style={{ fontSize: '14px', marginTop: '4px' }}
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? 'Less' : 'More'}
              </button>
            )}
          </div>
        </div>
        <div className="ticket-actions">
          <div className="ticket-pricing">
            <div className="ticket-price">
              {ticket.currency} {showPrice.toLocaleString()}
            </div>
            <div className="price-details d-flex align-items-center">
              {priceDetails}
              {priceDetails !== 'Free' && priceDetails !== 'No Tax' && (
                <div className="ms-2 position-relative">
                  <button
                    ref={ticketTooltipRef}
                    className="btn btn-link p-0 text-gray-500 hover-text-gray-700"
                    aria-label="Tax information"
                  >
                    <InfoCircle className="icon-sm" />
                  </button>
                </div>
              )}
            </div>
            <div className="max-tickets">
              Avail.{maxTickets} Ticket{ticket.maxTickets !== 1 ? 's' : ''}
            </div>
          </div>
         <div className="position-relative">
  {!ticket.isAdded && ticket.ticketQuantity === 0 ? (
    <button
      onClick={() => updateTicketQuantity(ticket.id, 1)}
      disabled={
        isCurrencyMismatch ||
        ticket.balance === 0 ||
        (!allowBuyers && hasOtherTicketsSelected)
      }
      onMouseEnter={() => {
        if (isCurrencyMismatch)
          setTooltip('Cannot select tickets with multiple currencies');
        else if (ticket.balance === 0)
          setTooltip('No tickets available');
        else if (!allowBuyers && hasOtherTicketsSelected)
          setTooltip('Only one ticket type is allowed for this event');
      }}
      onMouseLeave={() => setTooltip('')}
      className="btn btn-outline"
    >
      Add
    </button>
  ) : (
    <div className="quantity-controls d-flex align-items-center">
      {/* Always show minus button when ticket is added */}
      <button
        onClick={() => updateTicketQuantity(ticket.id, -1)}
        disabled={(ticket.ticketQuantity === 0 || isCurrencyMismatch)}
        onMouseEnter={() => {
          if (isCurrencyMismatch) {
            setTooltip('Cannot select tickets with multiple currencies');
          }
        }}
        onMouseLeave={() => setTooltip('')}
        className="quantity-btn"
      >
        <Minus className="icon-sm" />
      </button>

      {/* Show quantity */}
      <span className="quantity-display mx-2 fw-bold">
        {ticket.ticketQuantity}
      </span>

      {/* Show + button ONLY if allowMultipleTickets is true */}
      {allowMultipleTickets && (
        <button
          onClick={() => updateTicketQuantity(ticket.id, 1)}
          disabled={
            ticket.ticketQuantity >= maxTickets ||
            isCurrencyMismatch ||
            (!allowBuyers && hasOtherTicketsSelected)
          }
          onMouseEnter={() => {
            if (isCurrencyMismatch) {
              setTooltip('Cannot select tickets with multiple currencies');
            } else if (ticket.ticketQuantity >= maxTickets) {
              setTooltip('Maximum available quantity reached');
            } else if (!allowBuyers && hasOtherTicketsSelected) {
              setTooltip('Only one ticket type is allowed for this event');
            }
          }}
          onMouseLeave={() => setTooltip('')}
          className="quantity-btn"
        >
          <Plus className="icon-sm" />
        </button>
      )}
    </div>
  )}

  {/* Tooltip */}
  {tooltip &&
    (isCurrencyMismatch ||
      ticket.ticketQuantity >= maxTickets ||
      ticket.balance === 0 ||
      (!allowBuyers && hasOtherTicketsSelected)) && (
      <div className="tooltip position-absolute bg-gray-800 text-white text-sm rounded py-1 px-2 bottom-0 start-50 translate-middle-x z-10">
        {tooltip}
      </div>
    )}
</div>
        </div>
      </div>

      {ticket.isAdded &&
        ticket.guest_details?.ticket_name &&
        ticket.ticket_status === 'onsale' &&
        ticket.guest_details.ticket_status === 'onsale' && (
          <div className="guest-section">
            <div className="guest-divider"></div>
            <div className="guest-header">
              <button onClick={() => toggleGuestExpansion(ticket.id)} className="guest-toggle-btn btn">
                <UserPlus className="icon-sm me-1" />
                <span>Add Accompanying Person</span>
                {isGuestExpanded ? <ChevronUp className="icon-sm ms-1" /> : <ChevronDown className="icon-sm ms-1" />}
              </button>
            </div>

            {isGuestExpanded && (
              <div className="guest-details">
                <div className="guest-info">
                  <div className="guest-title">{ticket.guest_details.ticket_name}</div>
                  <div className="guest-description-container">
                    <p 
                      className="guest-subtitle" 
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: showFullDescription ? 'unset' : 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: showFullDescription ? 'normal' : 'normal',
                        lineHeight: '1.5em',
                        maxHeight: showFullDescription ? 'none' : '1.5em',
                      }}
                    >
                      {ticket.guest_details.description || ''}
                    </p>
                    {isGuestDescriptionLong && (
                      <button
                        className="btn btn-link p-0 text-primary"
                        style={{ fontSize: '14px', marginTop: '4px' }}
                        onClick={() => setShowFullDescription(!showFullDescription)}
                      >
                        {showFullDescription ? 'Less' : 'More'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="guest-actions">
                  <div className="guest-pricing">
                    <div className="guest-price">
                      {ticket.currency} {guestShowPrice.toLocaleString()}
                    </div>
                    <div className="price-details d-flex align-items-center">
                      {guestPriceDetails}
                      {guestPriceDetails !== 'No Tax' && (
                        <div className="ms-2 position-relative">
                          <button
                            ref={guestTooltipRef}
                            className="btn btn-link p-0 text-gray-500 hover-text-gray-700"
                            aria-label="Tax information"
                          >
                            <InfoCircle className="icon-sm" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="quantity-controls d-flex align-items-center">
                    <button
                      onClick={() => updateGuestCount(ticket.id, -1)}
                      disabled={ticket.guestCount === 0}
                      className="quantity-btn"
                    >
                      <Minus className="icon-sm" />
                    </button>
                    <span className="quantity-display mx-2">{ticket.guestCount}</span>
                    <button
                      onClick={() => updateGuestCount(ticket.id, 1)}
                      disabled={
                        ticket.guestCount >= (allowBuyers ? (ticket.guest_details.ticket_max_order || ticket.balance) : 1)
                      }
                      onMouseEnter={() => {
                        if (!allowBuyers && ticket.guestCount >= 1) {
                          setTooltip('Only one guest ticket is allowed for this event');
                        } else if (
                          allowBuyers &&
                          ticket.guestCount >= (ticket.guest_details.ticket_max_order || ticket.balance)
                        ) {
                          setTooltip('Maximum available guest quantity reached');
                        }
                      }}
                      onMouseLeave={() => setTooltip('')}
                      className="quantity-btn"
                    >
                      <Plus className="icon-sm" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
});

export default TicketCard;